import circleIcon from "../../../assets/icons/CircleWithThreeDots.svg";
import fileWithInfoIcon from "../../../assets/icons/fileWithInfo.svg";
import starIcon from "../../../assets/icons/StarandRec.svg";

export const TAB_REGIONAL = "regional";
export const TAB_NATIONAL = "national";
export const TAB_DEROGATION = "derogation";
export const TAB_TEMP_VAR = "temporaryVariation";

const normalizeAreaOfVariation = (value) =>
  String(value ?? "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim()
    .toLowerCase();

const isNationalOrProjectVariation = (row) => {
  const areaOfVariation = normalizeAreaOfVariation(row?.areaOfVariation);
  return !areaOfVariation || areaOfVariation.includes("national");
};

const splitVariationPendingChangeRows = (rows = []) => {
  const nationalRows = rows.filter(isNationalOrProjectVariation);
  const regionalRows = rows.filter((row) => !isNationalOrProjectVariation(row));

  return { nationalRows, regionalRows };
};

const getFirstTrackerReference = (rows = []) => {
  const match = rows.find((row) => String(row?.trackerReferenceNumber ?? "").trim());
  return String(match?.trackerReferenceNumber ?? "").trim();
};

const parseCreatedDate = (dateString) => {
  if (!dateString) return new Date(0);

  const [datePart, timePart = "00:00"] = String(dateString).split(" ");
  const [day, month, year] = datePart.split("/").map(Number);
  const [hours, minutes] = timePart.split(":").map(Number);

  return new Date(year, month - 1, day, hours ?? 0, minutes ?? 0);
};

const sortByCreatedDate = (rows = []) =>
  [...rows].sort((a, b) => parseCreatedDate(b?.createdDate) - parseCreatedDate(a?.createdDate));

const uniqueByTrackerReference = (rows = []) => {
  const seen = new Set();
  return rows.filter((row) => {
    const key = String(row?.trackerReferenceNumber ?? "").trim();
    if (!key || seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

const normalizeVariationType = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const getVariationType = (row) => {
  const typeOfVariationAppliedFor = normalizeVariationType(row?.typeOfVariationAppliedFor);
  if (row?.applicationApproved === true) {
    return normalizeVariationType(row?.typeApproved) || typeOfVariationAppliedFor;
  }

  return typeOfVariationAppliedFor;
};

const isDerogationVariation = (row) =>
  getVariationType(row).includes("derogation");

const isVariationPendingStandardsChange = (row) =>
  /variations? pending standards change/.test(getVariationType(row));

const isTemporaryVariation = (row) =>
  getVariationType(row).includes("temporary variation");

// Combine all variation buckets, then route rows by effective variation type.
const deriveVpscAndTempRows = (variationPendingChange = [], temporaryVariation = [], derogation = []) => {
  const combined = [
    ...variationPendingChange,
    ...temporaryVariation,
    ...derogation,
  ];

  return {
    tempRows: uniqueByTrackerReference(sortByCreatedDate(combined.filter(isTemporaryVariation))),
    derogationRows: uniqueByTrackerReference(sortByCreatedDate(combined.filter(isDerogationVariation))),
    vpscRows: uniqueByTrackerReference(sortByCreatedDate(combined.filter(isVariationPendingStandardsChange))),
  };
};

export const getVariationTabs = ({
  variationPendingChange = [],
  derogation = [],
  temporaryVariation = [],
}) => {
  const { tempRows, derogationRows, vpscRows } = deriveVpscAndTempRows(
    variationPendingChange,
    temporaryVariation,
    derogation,
  );

  const { nationalRows, regionalRows } = splitVariationPendingChangeRows(vpscRows);

  return [
    {
      key: TAB_REGIONAL,
      icon: circleIcon,
      label: "Variation Pending Standards Change – Regional",
      count: regionalRows.length,
      rows: sortByCreatedDate(regionalRows),
      defaultTrackerReferenceNumber: getFirstTrackerReference(regionalRows),
      tableTitle: "Variation Pending Standards Change – Regional",
    },
    {
      key: TAB_NATIONAL,
      icon: circleIcon,
      label: "Variation Pending Standards Change – National/Project",
      count: nationalRows.length,
      rows: sortByCreatedDate(nationalRows),
      defaultTrackerReferenceNumber: getFirstTrackerReference(nationalRows),
      tableTitle: "Variation Pending Standards Change – National/Project",
    },
    {
      key: TAB_DEROGATION,
      icon: fileWithInfoIcon,
      label: "Derogations",
      count: derogationRows.length,
      rows: derogationRows,
      defaultTrackerReferenceNumber: getFirstTrackerReference(derogationRows),
      tableTitle: "Derogations",
    },
    {
      key: TAB_TEMP_VAR,
      icon: starIcon,
      label: "Temporary Variations",
      count: tempRows.length,
      rows: tempRows,
      defaultTrackerReferenceNumber: getFirstTrackerReference(tempRows),
      tableTitle: "Temporary Variations",
    },
  ];
};

